import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';
import Button, { ButtonProps } from '@/components/molecules/button/button';
import SearchBarSection from '@/components/molecules/search-bar-section/search-bar-section';
import {
  LocationChangeParameters,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import { RoomsState } from '@/components/organisms/search-bar/search-bar.types';
import SearchBarDropdown from '../search-bar-dropdown/search-bar-dropdown';
import styles from './search-bar-wrapper.module.scss';

export interface SearchBarWrapperProps {
  /** The active section id */
  activeSection?: number;
  /** Label to show in the button  */
  buttonLabel: string;
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** Indicates whether the search button is enabled  */
  canSubmit?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether to show the compact version */
  compact?: boolean;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Indicates whether the date section is loading */
  isLoadingDate?: boolean;
  /** Indicates whether the locations section is loading */
  isLoadingLocations?: boolean;
  /** Indicates whether the search bar is used in the search widget */
  isWidget?: boolean;
  /** An event that is triggered on date change */
  onChangeDate?: (dates: Date[]) => void;
  /** An event that is triggered on duration change */
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on location change */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on persons change */
  onChangePersons?: (roomsState: RoomsState) => void;
  /** An event that is triggered on transportation change */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on close click */
  onClickClose?: () => void;
  /** An event that is triggered on next click */
  onClickNext?: () => void;
  /** An event that is triggered on close click */
  onClickSearch?: ButtonProps['onClick'];
  /** An event that is triggered on search bar section click */
  onClickSection?: (id: number) => void;
  /** The search settings */
  searchSettings?: Content.Components['SearchSettingsDto'];
  /** Sections to render in the search bar */
  sections?: SearchBarWrapperSectionData[];
}

const SearchBarWrapper = memo(
  ({
    activeSection = -1,
    buttonLabel,
    buttonMode = false,
    canSubmit = false,
    className,
    customRangeStyle = false,
    isLoadingDate = false,
    isLoadingLocations = false,
    isWidget = false,
    onChangeDate,
    onChangeDuration,
    onChangeLocations,
    onChangePersons,
    onChangeTransportation,
    onClickBack,
    onClickClose,
    onClickNext,
    onClickSearch,
    onClickSection,
    searchSettings,
    sections = [],
    ...rest
  }: SearchBarWrapperProps) => {
    const activeSectionType = useMemo(
      () => sections.find(section => section.id === activeSection)?.type,
      [sections, activeSection],
    );

    const isSquareCorner =
      activeSectionType === SearchBarWrapperSectionType.LOCATIONS ||
      activeSectionType === SearchBarWrapperSectionType.DATE;

    const renderSection = useCallback(
      (section: SearchBarWrapperSectionData) => (
        <div
          key={section.id}
          className={classNames(styles.searchSectionWrapper, {
            [styles.isActive]: section.id === activeSection,
            [styles.isSquareCorner]:
              activeSectionType === SearchBarWrapperSectionType.LOCATIONS ||
              (isWidget && activeSection !== -1),
          })}
        >
          <SearchBarSection
            className={styles.searchSection}
            disabled={section.disabled}
            isActive={section.id === activeSection}
            isLoading={section.isLoading}
            isNotified={section.isNotified}
            label={section.label}
            onClick={() => onClickSection?.(section.id)}
            placeholder={section.placeholder}
            value={section.value}
          />
        </div>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [activeSection, activeSectionType, isWidget],
    );

    return (
      <div
        {...rest}
        className={classNames(styles.component, className, {
          [styles.isActive]: activeSection !== -1,
          [styles.isSearchWidget]: isWidget && !buttonMode,
        })}
      >
        <div
          className={classNames(styles.searchBarWrapper, {
            [styles.isSearchWidget]: isWidget,
            [styles.isSquareCorner]: isSquareCorner,
          })}
        >
          {sections.map(renderSection)}

          {!isWidget && (
            <Button
              className={classNames(styles.button, {
                [styles.isSquareCorner]: isSquareCorner,
              })}
              disabled={!canSubmit}
              onClick={onClickSearch}
              text={buttonLabel}
            />
          )}
        </div>

        {!buttonMode && (
          <SearchBarDropdown
            activeSection={activeSection}
            customRangeStyle={customRangeStyle}
            isLoadingDate={isLoadingDate}
            isLoadingLocations={isLoadingLocations}
            isWidget={isWidget}
            onChangeDate={onChangeDate}
            onChangeDuration={onChangeDuration}
            onChangeLocations={onChangeLocations}
            onChangePersons={onChangePersons}
            onChangeTransportation={onChangeTransportation}
            onClickBack={onClickBack}
            onClickClose={onClickClose}
            onClickNext={onClickNext}
            onClickSearch={onClickSearch}
            searchSettings={searchSettings}
            sections={sections}
          />
        )}
      </div>
    );
  },
);

SearchBarWrapper.displayName = 'SearchBarWrapper';

export default SearchBarWrapper;
